import React, { useState, useMemo, useContext } from 'react';
import { Dropdown } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';

// Reusable Single Select Dropdown with optional search functionality
const SearchableFilterDropdown = ({
  optionList = [], // Array of option objects
  selectedData = null,
  // default select which return null (optional)
  allItemsLabel = null,
  onFilterChange,
  placeholder,
  noResultsText = 'No items found',
  menuMinWidth = '220px',
  showSearch = true,
  toggleContent, // Custom content for toggle button (e.g., icon)
  count, // Optional count shown in toggle as (N)
  valueKey = '_id', // key used as unique value (id)
  labelKey = 'label' // key used for display label
}) => {
  const { translate } = useContext(LocalizeContext);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options based on search query (only if search is enabled)
  const filteredOptions = useMemo(() => {
    if (!optionList || optionList.length === 0) return [];

    if (!showSearch) {
      return optionList; // Return all options when search is disabled
    }

    const filtered = optionList.filter(option => {
      const displayValue = option?.[labelKey] || '';
      return displayValue.toLowerCase().includes(searchQuery.toLowerCase().trim());
    });

    return filtered;
  }, [optionList, searchQuery, showSearch]);

  const handleItemClick = item => {
    onFilterChange(item);
    setSearchQuery('');
  };

  const selectedDisplayValue = selectedData ? selectedData?.[labelKey] || '' : allItemsLabel || '';
  const defaultPlaceholder = placeholder || translate('search_users') || 'Search...';

  return (
    <Dropdown>
      <Dropdown.Toggle
        size="sm"
        block
        variant={selectedData ? 'primary' : 'outline-primary'}
        id={'searchable-filter-dropdown'}
        className={selectedData ? '' : 'searchable-filter-toggle-outline'}
      >
        {toggleContent || (
          <>
            {selectedDisplayValue}
            {count != null && (
              <span className={selectedData ? 'text-white tiny' : 'tiny'}> ({count})</span>
            )}
          </>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ minWidth: menuMinWidth, padding: 0 }}>
        {/* Search input box at the top - only shown if showSearch is true */}
        {showSearch && (
          <div
            className="px-3 py-2 border-bottom"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            style={{ backgroundColor: '#f8f9fa' }}
          >
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={defaultPlaceholder}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onClick={e => e.stopPropagation()}
              onFocus={e => e.stopPropagation()}
              autoComplete="off"
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* All Items - only visible if allItemsLabel is provided */}
        {allItemsLabel && (
          <>
            <Dropdown.Item
              onClick={() => handleItemClick(null)}
              active={!selectedData}
              style={{ fontWeight: selectedData ? 'normal' : 'bold' }}
            >
              {allItemsLabel}
            </Dropdown.Item>
            <Dropdown.Divider style={{ margin: 0 }} />
          </>
        )}

        {/* Filtered options list */}
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => {
              const optionId = option?.[valueKey];
              const isActive = selectedData && selectedData?.[valueKey] === optionId;
              return (
                <Dropdown.Item
                  key={optionId}
                  onClick={() => handleItemClick(option)}
                  active={isActive}
                  className={isActive ? 'bg-primary text-white' : ''}
                >
                  {option?.[labelKey] || ''}
                </Dropdown.Item>
              );
            })
          ) : showSearch && searchQuery ? (
            <Dropdown.Item disabled className="text-muted text-center py-2">
              {noResultsText}
            </Dropdown.Item>
          ) : null}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SearchableFilterDropdown;
